/**
 * Display helpers. Presentation only - nothing here is ever parsed back.
 *
 * What is NOT here, on purpose: money formatting (`formatCents`), ratios
 * (`formatRatio`), week labels (`formatWeekLabel`) and every lane/stage label.
 * Those live in `@botguide/shared` because the API prints them too, and a
 * second copy is how the two start disagreeing. Import them from the shared
 * package directly.
 */
import { BUSINESS_TIMEZONE } from '@botguide/shared';
import type { PlainDate, Role, TimeEntryStatus } from '@botguide/shared';

/** An em dash. One constant so "no value" looks identical everywhere. */
export const EMPTY = '—';

/* -------------------------------------------------------------------------- */
/* Dates                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * A `PlainDate` is a calendar day with no zone. Rendering it through the local
 * timezone is the classic off-by-one: `new Date('2026-01-05')` is midnight UTC,
 * which is 5 January in Denver but 4 January is what you get if you format it
 * anywhere west of UTC without pinning the zone. So: pin it to UTC.
 */
function asUtcInstant(date: PlainDate): Date {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1));
}

const dayFormatter = new Intl.DateTimeFormat('en-US', {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
  timeZone: 'UTC',
});

const shortDayFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  timeZone: 'UTC',
});

/** `2026-01-05` -> `Mon, Jan 5`. */
export function formatDay(date: PlainDate | null): string {
  return date ? dayFormatter.format(asUtcInstant(date)) : EMPTY;
}

/** `2026-01-05` -> `Jan 5`. For table cells where the weekday is noise. */
export function formatDayShort(date: PlainDate | null): string {
  return date ? shortDayFormatter.format(asUtcInstant(date)) : EMPTY;
}

const instantFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  timeZone: BUSINESS_TIMEZONE,
});

/**
 * An ISO instant in Mountain Time. The business runs on one clock, so two
 * people looking at the same "approved at" see the same string.
 */
export function formatInstant(iso: string | null): string {
  if (!iso) return EMPTY;
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? EMPTY : instantFormatter.format(parsed);
}

const relativeFormatter = new Intl.RelativeTimeFormat('en-US', { numeric: 'auto' });

const RELATIVE_STEPS: readonly [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 365 * 86_400_000],
  ['month', 30 * 86_400_000],
  ['week', 7 * 86_400_000],
  ['day', 86_400_000],
  ['hour', 3_600_000],
  ['minute', 60_000],
];

/** `"3 days ago"`, `"in 2 hours"`, `"now"`. */
export function formatRelative(iso: string | null, now: Date = new Date()): string {
  if (!iso) return EMPTY;
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return EMPTY;

  const deltaMs = parsed.getTime() - now.getTime();
  for (const [unit, ms] of RELATIVE_STEPS) {
    if (Math.abs(deltaMs) >= ms) return relativeFormatter.format(Math.round(deltaMs / ms), unit);
  }
  return 'now';
}

/* -------------------------------------------------------------------------- */
/* Quantities                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Minutes as decimal hours: `90` -> `1.5h`.
 *
 * Decimal rather than `1h 30m` because hours are logged in quarter-hour steps
 * and summed into invoices - a column of `1.5` adds up by eye, a column of
 * `1h 30m` does not.
 */
export function formatHours(minutes: number | null): string {
  if (minutes === null) return EMPTY;
  const hours = minutes / 60;
  return `${Number.isInteger(hours) ? hours : hours.toFixed(2).replace(/0$/, '')}h`;
}

/** Story points, on Cody's half-step scale. An unestimated task is a dash. */
export function formatPoints(points: number | null): string {
  if (points === null) return EMPTY;
  return Number.isInteger(points) ? String(points) : points.toFixed(1);
}

/** A 0-1 progress fraction as a whole percentage. */
export function formatProgress(fraction: number): string {
  return `${Math.round(Math.min(Math.max(fraction, 0), 1) * 100)}%`;
}

/** `formatCount(1, 'task')` -> `1 task`; `formatCount(3, 'task')` -> `3 tasks`. */
export function formatCount(n: number, singular: string, plural = `${singular}s`): string {
  return `${n} ${n === 1 ? singular : plural}`;
}

/* -------------------------------------------------------------------------- */
/* People                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Up to two initials for an avatar. Falls back to the first character of
 * whatever we were given so a one-word name still renders something.
 */
export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  const first = words[0] ?? '';
  const last = words.length > 1 ? (words[words.length - 1] ?? '') : '';
  return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase() || '?';
}

/**
 * A stable 0-359 hue from a person's id, so the same person is the same colour
 * on every screen and in every session without storing anything.
 */
export function hueFromId(id: string): number {
  let hash = 0;
  for (let i = 0; i < id.length; i += 1) hash = (hash * 31 + id.charCodeAt(i)) % 360;
  return hash;
}

export const ROLE_LABELS: Record<Role, string> = {
  owner: 'Owner',
  contractor: 'Contractor',
  client: 'Client',
};

export const TIME_ENTRY_STATUS_LABELS: Record<TimeEntryStatus, string> = {
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
};
